import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import { Search, Refresh, Download } from '@mui/icons-material';
import { format, subDays } from 'date-fns';
import { pl } from 'date-fns/locale';
import config from '../config/environment';
import apiClient from '../shared/services/api';

const Rozliczenia = () => {
  const [searchParams, setSearchParams] = useState({
    fromUtc: subDays(new Date(), 30),
    toUtc: new Date(),
  });
  
  const [orderProfit, setOrderProfit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totals, setTotals] = useState({
    totalUnitPrice: 0,
    totalPurchasePrice: 0,
    totalProfit: 0
  });

  const [additionalItems, setAdditionalItems] = useState([]);
  const [newItem, setNewItem] = useState({
    name: '',
    amount: 0,
    type: 'cost',
    percentage: '',
    customPercentage: 0
  });

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.post('/orders/', {
        fromUtc: searchParams.fromUtc.toISOString(),
        toUtc: searchParams.toUtc.toISOString(),
      });

      const data = response.data;
      setOrderProfit(data.orderProfit || []);
      setTotals({
        totalUnitPrice: data.totalUnitPrice || 0,
        totalPurchasePrice: data.totalPurchasePrice || 0,
        totalProfit: data.totalProfit || 0
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSearchParams({
      fromUtc: subDays(new Date(), 30),
      toUtc: new Date(),
    });
    setOrderProfit([]);
    setError(null);
    setTotals({
      totalUnitPrice: 0,
      totalPurchasePrice: 0,
      totalProfit: 0
    });
    setAdditionalItems([]);
  };

  const handleAddItem = () => {
    if (!newItem.name || newItem.amount === 0) return;
    
    let finalAmount = newItem.amount;
    
    // Jeśli wybrano procent, oblicz kwotę na podstawie wartości sprzedaży
    if (newItem.percentage && newItem.percentage !== 'custom') {
      const percentage = parseFloat(newItem.percentage);
      finalAmount = (totals.totalUnitPrice * percentage) / 100;
    } else if (newItem.percentage === 'custom' && newItem.customPercentage > 0) {
      const percentage = newItem.customPercentage;
      finalAmount = (totals.totalUnitPrice * percentage) / 100;
    }
    
    const itemToAdd = {
      ...newItem,
      amount: finalAmount,
      id: Date.now()
    };
    
    setAdditionalItems(prev => [...prev, itemToAdd]);
    setNewItem({
      name: '',
      amount: 0,
      type: 'cost',
      percentage: '',
      customPercentage: 0
    });
  };

  const handleRemoveItem = (index) => {
    setAdditionalItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleQuickAdd = (name, amount, type, percentage) => {
    setNewItem({
      name,
      amount,
      type,
      percentage,
      customPercentage: 0
    });
  };

  // Obliczenia końcowe
  const totalCosts = additionalItems
    .filter(item => item.type === 'cost')
    .reduce((sum, item) => sum + item.amount, 0);

  const totalProfits = additionalItems
    .filter(item => item.type === 'profit')
    .reduce((sum, item) => sum + item.amount, 0);

  const finalProfit = totals.totalProfit - totalCosts + totalProfits;



  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Export functionality to be implemented');
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm', { locale: pl });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
    }).format(amount);
  };

  const formatDateForInput = (date) => {
    return format(date, 'yyyy-MM-dd');
  };

  const handleDateChange = (field, value) => {
    const newDate = new Date(value);
    setSearchParams(prev => ({ ...prev, [field]: newDate }));
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
        Rozliczenia
      </Typography>

      {/* Search Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Wyszukiwanie zamówień
        </Typography>
        
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Data od"
              type="date"
              value={formatDateForInput(searchParams.fromUtc)}
              onChange={(e) => handleDateChange('fromUtc', e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Data do"
              type="date"
              value={formatDateForInput(searchParams.toUtc)}
              onChange={(e) => handleDateChange('toUtc', e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          
                     <Grid item xs={12} md={4}>
             <Box sx={{ display: 'flex', gap: 1 }}>
               <Button
                 variant="contained"
                 startIcon={<Search />}
                 onClick={handleSearch}
                 disabled={loading}
                 sx={{ flex: 1 }}
               >
                 Szukaj
               </Button>
               <Button
                 variant="outlined"
                 startIcon={<Refresh />}
                 onClick={handleReset}
                 disabled={loading}
                 sx={{ flex: 1 }}
               >
                 Reset
               </Button>
             </Box>
           </Grid>
        </Grid>
      </Paper>

      {/* Results Summary */}
      {totals.totalUnitPrice > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="h6">
                  Łączna wartość sprzedaży: {formatCurrency(totals.totalUnitPrice)}
                </Typography>
                <Typography variant="h6">
                  Łączna wartość zakupu: {formatCurrency(totals.totalPurchasePrice)}
                </Typography>
                <Typography variant="h6">
                  Łączny zysk: {formatCurrency(totals.totalProfit)}
                </Typography>
                <Typography variant="h6">
                  Łączna liczba elementów: {orderProfit.reduce((total, shop) => total + (shop.orderProfitItems?.length || 0), 0)}
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={handleExport}
                >
                  Eksportuj
                </Button>
              </Grid>
            </Grid>
          </CardContent>
                  </Card>
       )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Błąd podczas pobierania danych: {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Results Tables - Grouped by Shops */}
      {orderProfit.length > 0 && !loading && (
        <>
          {orderProfit.map((shopData) => (
            <Paper key={shopData.shopId} sx={{ width: '100%', overflow: 'hidden', mb: 3 }}>
              <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'white' }}>
                <Typography variant="h6">
                  Sklep: {shopData.shopName} (ID: {shopData.shopId})
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Łączna wartość sprzedaży: {formatCurrency(shopData.totalUnitPrice)} | 
                  Łączna wartość zakupu: {formatCurrency(shopData.totalPurchasePrice)} | 
                  Łączny zysk: {formatCurrency(shopData.totalProfit)} | 
                  Liczba elementów: {shopData.orderProfitItems?.length || 0}
                </Typography>
              </Box>
              
              <TableContainer>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID Zamówienia</TableCell>
                      <TableCell>ID Oferty</TableCell>
                      <TableCell>Nazwa produktu</TableCell>
                      <TableCell>Ilość</TableCell>
                      <TableCell>Cena jednostkowa</TableCell>
                      <TableCell>Cena zakupu</TableCell>
                      <TableCell>Zysk jednostkowy</TableCell>
                      <TableCell>Łączny zysk</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shopData.orderProfitItems.map((item) => (
                      <TableRow key={item.checkoutFormId} hover>
                        <TableCell>{item.checkoutFormId}</TableCell>
                        <TableCell>{item.offerId}</TableCell>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{formatCurrency(item.unitPrice)}</TableCell>
                        <TableCell>{formatCurrency(item.purchasePrice)}</TableCell>
                        <TableCell>{formatCurrency(item.unitProfit)}</TableCell>
                        <TableCell>{formatCurrency(item.totalProfit)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
        </>
      )}

             {/* Final Summary */}
       {orderProfit.length > 0 && !loading && (
         <Paper sx={{ p: 3, backgroundColor: 'success.light', color: 'success.contrastText' }}>
           <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', fontWeight: 'bold' }}>
             PODSUMOWANIE OGÓLNE
           </Typography>
           <Grid container spacing={3} justifyContent="center">
             <Grid item xs={12} sm={4}>
               <Box sx={{ textAlign: 'center' }}>
                 <Typography variant="h6" color="inherit">
                   Łączna wartość sprzedaży
                 </Typography>
                 <Typography variant="h4" color="inherit" sx={{ fontWeight: 'bold' }}>
                   {formatCurrency(totals.totalUnitPrice)}
                 </Typography>
               </Box>
             </Grid>
             <Grid item xs={12} sm={4}>
               <Box sx={{ textAlign: 'center' }}>
                 <Typography variant="h6" color="inherit">
                   Łączna wartość zakupu
                 </Typography>
                 <Typography variant="h4" color="inherit" sx={{ fontWeight: 'bold' }}>
                   {formatCurrency(totals.totalPurchasePrice)}
                 </Typography>
               </Box>
             </Grid>
             <Grid item xs={12} sm={4}>
               <Box sx={{ textAlign: 'center' }}>
                 <Typography variant="h6" color="inherit">
                   Łączny zysk
                 </Typography>
                 <Typography variant="h4" color="inherit" sx={{ fontWeight: 'bold' }}>
                   {formatCurrency(totals.totalProfit)}
                 </Typography>
               </Box>
             </Grid>
           </Grid>
         </Paper>
       )}

       {/* Additional Costs and Profits Management */}
       {orderProfit.length > 0 && !loading && (
         <Paper sx={{ p: 3, mb: 3 }}>
           <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
             Zarządzanie dodatkowymi kosztami i zyskami
           </Typography>
           
           {/* Add new cost/profit form */}
           <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 3 }}>
             <Grid item xs={12} sm={3}>
               <TextField
                 label="Nazwa"
                 value={newItem.name}
                 onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
                 fullWidth
                 size="small"
                 placeholder="np. Podatek 23%, Prowizja Allegro"
               />
             </Grid>
             <Grid item xs={12} sm={2}>
               <TextField
                 label="Kwota (zł)"
                 type="number"
                 value={newItem.amount}
                 onChange={(e) => setNewItem(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                 fullWidth
                 size="small"
                 placeholder="0.00"
               />
             </Grid>
             <Grid item xs={12} sm={2}>
               <FormControl fullWidth size="small">
                 <InputLabel>Typ</InputLabel>
                 <Select
                   value={newItem.type}
                   onChange={(e) => setNewItem(prev => ({ ...prev, type: e.target.value }))}
                   label="Typ"
                 >
                   <MenuItem value="cost">Koszt</MenuItem>
                   <MenuItem value="profit">Zysk</MenuItem>
                 </Select>
               </FormControl>
             </Grid>
             <Grid item xs={12} sm={2}>
               <FormControl fullWidth size="small">
                 <InputLabel>Procent</InputLabel>
                 <Select
                   value={newItem.percentage}
                   onChange={(e) => setNewItem(prev => ({ ...prev, percentage: e.target.value }))}
                   label="Procent"
                 >
                   <MenuItem value="">Brak</MenuItem>
                   <MenuItem value="23">23% (VAT)</MenuItem>
                   <MenuItem value="15">15% (Prowizja Allegro)</MenuItem>
                   <MenuItem value="19">19% (Inne)</MenuItem>
                   <MenuItem value="custom">Własny %</MenuItem>
                 </Select>
               </FormControl>
             </Grid>
             <Grid item xs={12} sm={2}>
               <TextField
                 label="Własny %"
                 type="number"
                 value={newItem.customPercentage}
                 onChange={(e) => setNewItem(prev => ({ ...prev, customPercentage: parseFloat(e.target.value) || 0 }))}
                 fullWidth
                 size="small"
                 placeholder="0"
                 disabled={newItem.percentage !== 'custom'}
               />
             </Grid>
             <Grid item xs={12} sm={1}>
               <Button
                 variant="contained"
                 onClick={handleAddItem}
                 disabled={!newItem.name || newItem.amount === 0}
                 fullWidth
                 size="small"
               >
                 +
               </Button>
             </Grid>
           </Grid>

           {/* Quick add buttons */}
           <Box sx={{ mb: 3 }}>
             <Typography variant="body2" gutterBottom>
               Szybkie dodawanie:
             </Typography>
             <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
               <Button
                 variant="outlined"
                 size="small"
                 onClick={() => handleQuickAdd('Podatek 23%', 0, 'cost', '23')}
               >
                 + Podatek 23%
               </Button>
               <Button
                 variant="outlined"
                 size="small"
                 onClick={() => handleQuickAdd('Prowizja Allegro', 0, 'cost', '15')}
               >
                 + Prowizja Allegro 15%
               </Button>
               <Button
                 variant="outlined"
                 size="small"
                 onClick={() => handleQuickAdd('Zysk z innego sklepu', 2000, 'profit', '')}
               >
                 + Zysk +2000 zł
               </Button>
             </Box>
           </Box>

           {/* Current items list */}
           {additionalItems.length > 0 && (
             <Box sx={{ mb: 3 }}>
               <Typography variant="body2" gutterBottom>
                 Dodane koszty i zyski:
               </Typography>
               <Grid container spacing={1}>
                 {additionalItems.map((item, index) => (
                   <Grid item xs={12} sm={6} md={4} key={index}>
                     <Chip
                       label={`${item.name}: ${item.type === 'cost' ? '-' : '+'}${formatCurrency(item.amount)}`}
                       color={item.type === 'cost' ? 'error' : 'success'}
                       onDelete={() => handleRemoveItem(index)}
                       variant="outlined"
                       sx={{ width: '100%', justifyContent: 'space-between' }}
                     />
                   </Grid>
                 ))}
               </Grid>
             </Box>
           )}

           {/* Final calculation */}
           <Paper sx={{ p: 2, backgroundColor: 'grey.100' }}>
             <Grid container spacing={2} alignItems="center">
               <Grid item xs={12} sm={3}>
                 <Typography variant="body2">
                   Zysk podstawowy: {formatCurrency(totals.totalProfit)}
                 </Typography>
               </Grid>
               <Grid item xs={12} sm={3}>
                 <Typography variant="body2" color="error.main">
                   Suma kosztów: {formatCurrency(totalCosts)}
                 </Typography>
               </Grid>
               <Grid item xs={12} sm={3}>
                 <Typography variant="body2" color="success.main">
                   Suma zysków: {formatCurrency(totalProfits)}
                 </Typography>
               </Grid>
               <Grid item xs={12} sm={3}>
                 <Typography variant="h6" fontWeight="bold">
                   Zysk końcowy: {formatCurrency(finalProfit)}
                 </Typography>
               </Grid>
             </Grid>
           </Paper>
         </Paper>
       )}

      {/* No Results */}
      {orderProfit.length === 0 && !loading && !error && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            Brak danych do wyświetlenia
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Użyj formularza wyszukiwania, aby znaleźć dane
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default Rozliczenia; 